import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as action from '../../../Action/BulkSeriesImport/BulkSeriesImport_Action';
import { EPISODE_STATUS, DECISION, StatusBadge } from './status';
import { errorMessage, fileName } from './helpers';
import Thumbnail from './Thumbnail';
import JobProgress from './JobProgress';

const PER_PAGE = 50;
const COLUMNS = '32px 76px 44px 72px minmax(0,1.2fr) 150px 120px minmax(0,1.6fr)';

/**
 * Episodes of one series item. Before start it shows what start will do to
 * each file; after start, live status with failed episodes first, and lets
 * the admin pick failed ones to retry individually.
 */
const EpisodeTable = ({ batchId, seriesItem, started, canManage, refreshKey, onChanged }) => {
    const dispatch = useDispatch();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [picked, setPicked] = useState([]);
    const [retrying, setRetrying] = useState(false);

    const fetchPage = useCallback((page, perPage = PER_PAGE) => dispatch(action.getBulkImportEpisodeItems(seriesItem.id, {
        page, per_page: perPage, sort: started ? 'failed_first' : 'number',
    })), [dispatch, seriesItem.id, started]);

    // Initial load, and a reload of what's on screen whenever the batch page reloads (Refresh, or after an action).
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const shown = Math.min(100, Math.max(PER_PAGE, rows.length));
        fetchPage(1, shown)
            .then((res) => {
                if (cancelled) return;
                const list = res?.responseDetails || [];
                setRows(Array.from(list));
                setTotal(list.totalRecords ?? list.length);
            })
            .catch((err) => {
                if (!cancelled) {
                    setRows((prev) => prev.map((row) => ({ ...row, job_percent_complete: null, job_status: null })));
                    toast.error(errorMessage(err, 'Failed to load episodes'));
                }
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- rows.length read once per refresh on purpose
    }, [fetchPage, refreshKey]);

    const showMore = () => {
        const nextPage = Math.floor(rows.length / PER_PAGE) + 1;
        fetchPage(nextPage).then((res) => {
            const list = Array.from(res?.responseDetails || []);
            setRows((prev) => [...prev, ...list.filter((r) => !prev.some((p) => p.id === r.id))]);
        }).catch((err) => toast.error(errorMessage(err, 'Failed to load episodes')));
    };

    const toggle = (id) => setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

    const retry = () => {
        setRetrying(true);
        dispatch(action.retryBulkImportEpisodes(batchId, { episode_item_ids: picked }))
            .then((res) => {
                const { retried = [], skipped = [] } = res?.responseDetails || {};
                if (retried.length) toast.success(`${retried.length} episode${retried.length === 1 ? '' : 's'} re-queued`);
                skipped.forEach((s) => toast.warn(`Episode item ${s.episodeItemId}: ${s.reason}`));
                setPicked([]);
                onChanged();
            })
            .catch((err) => toast.error(errorMessage(err, 'Failed to retry episodes')))
            .finally(() => setRetrying(false));
    };

    const retryable = rows.filter((r) => r.retryable);

    return (
        <div style={{ border: '1px solid rgba(255,255,255,.08)', borderRadius: 12, overflow: 'hidden', background: '#0e0e18' }}>
            {started && canManage && retryable.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '10px 14px' }}>
                    <span className="bsi-muted" style={{ fontSize: 13 }}>Failed episodes are listed first. Pick the ones to resubmit.</span>
                    <button type="button" className="bsi-btn outline sm" disabled={retrying || picked.length === 0} onClick={retry}>
                        {retrying ? <span className="spinner-border spinner-border-sm" /> : `Retry ${picked.length || ''} selected episode${picked.length === 1 ? '' : 's'}`}
                    </button>
                </div>
            )}
            <div className="bsi-row head" style={{ gridTemplateColumns: COLUMNS, padding: '10px 14px' }}>
                <span /><span className="bsi-th">Thumb</span><span className="bsi-th">#</span>
                <span className="bsi-th">ID</span><span className="bsi-th">Source file</span><span className="bsi-th">Status</span>
                <span className="bsi-th">Job percent complete</span><span className="bsi-th">Error</span>
            </div>
            {loading && <div className="bsi-row bsi-muted" style={{ padding: '10px 14px' }}>Loading…</div>}
            {!loading && rows.length === 0 && <div className="bsi-row bsi-muted" style={{ padding: '10px 14px' }}>No episodes.</div>}
            {rows.map((ep) => {
                const decision = !started && ep.status === 'DISCOVERED' ? DECISION[ep.decision] : null;
                const thumbnailReady = ep.live_transcoding_status === 'complete' || ['COMPLETE', 'IGNORED_EXISTING_PATH'].includes(ep.status);
                return (
                    <div key={ep.id} className="bsi-row" style={{ gridTemplateColumns: COLUMNS, padding: '8px 14px', fontSize: 13, background: picked.includes(ep.id) ? 'rgba(0,168,243,.05)' : undefined }}>
                        {started && canManage && ep.retryable
                            ? <input type="checkbox" className="bsi-cb" checked={picked.includes(ep.id)} onChange={() => toggle(ep.id)} aria-label={`Retry episode ${ep.episode_number}`} />
                            : <span />}
                        <Thumbnail key={`${ep.live_thumbnail_url}:${refreshKey}`} src={thumbnailReady ? ep.live_thumbnail_url : null}
                            alt={`Episode ${ep.episode_number} thumbnail`} />
                        <span className="bsi-title">{ep.episode_number}</span>
                        {/* The episode's real ID — assigned once the import starts. */}
                        <span className="bsi-mono" style={{ color: ep.episode_id ? '#c8c8d8' : '#5a5a6a' }}>{ep.episode_id ? ep.episode_id : '—'}</span>
                        <span className="bsi-mono" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ep.source_uri}>{fileName(ep.source_uri)}</span>
                        <span>
                            {decision
                                ? <span className={`bsi-badge ${decision[1]}`}>{decision[0]}</span>
                                : <StatusBadge map={EPISODE_STATUS} status={ep.status} />}
                        </span>
                        <JobProgress item={ep} loading={loading} />
                        <span style={{ color: ep.error_message ? '#ffc9c3' : '#5a5a6a', lineHeight: 1.4 }}>
                            {ep.error_message || '—'}
                            {ep.status === 'FAILED' && !ep.retryable && ep.retry_hint && <span className="bsi-muted" style={{ display: 'block' }}>{ep.retry_hint}</span>}
                        </span>
                    </div>
                );
            })}
            {rows.length < total && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 10, borderTop: '1px solid rgba(255,255,255,.06)' }}>
                    <button type="button" className="bsi-btn ghost sm" onClick={showMore}>Show {Math.min(PER_PAGE, total - rows.length)} more</button>
                </div>
            )}
        </div>
    );
};

export default EpisodeTable;
